import { mat4LookAt, mat4Multiply, mat4Perspective, vec3, type Vec3 } from './math';

export class OrbitCamera {
  target: Vec3 = vec3(0, 0, 0);
  distance = 1.5;
  azimuth = Math.PI / 4;
  elevation = 0.6;
  fov = 45 * Math.PI / 180;
  near = 0.001;
  far = 100;

  position(): Vec3 {
    const cosElev = Math.cos(this.elevation);
    return {
      x: this.target.x + this.distance * cosElev * Math.sin(this.azimuth),
      y: this.target.y + this.distance * Math.sin(this.elevation),
      z: this.target.z + this.distance * cosElev * Math.cos(this.azimuth),
    };
  }

  viewMatrix(): Float32Array {
    return mat4LookAt(this.position(), this.target, vec3(0, 1, 0));
  }

  projectionMatrix(aspect: number): Float32Array {
    return mat4Perspective(this.fov, aspect, this.near, this.far);
  }

  viewProjection(aspect: number): Float32Array {
    return mat4Multiply(this.projectionMatrix(aspect), this.viewMatrix());
  }

  /** Camera forward direction (normalized, world-space). */
  direction(): Vec3 {
    const p = this.position();
    const dx = this.target.x - p.x;
    const dy = this.target.y - p.y;
    const dz = this.target.z - p.z;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return len > 1e-10 ? vec3(dx / len, dy / len, dz / len) : vec3(0, -1, 0);
  }

  rotate(dx: number, dy: number): void {
    this.azimuth -= dx * 0.005;
    this.elevation += dy * 0.005;
    this.elevation = Math.max(-1.4, Math.min(1.4, this.elevation));
  }

  zoom(delta: number): void {
    this.distance *= Math.max(0.1, 1.0 - delta * 0.1);
    this.distance = Math.max(0.01, Math.min(200, this.distance));
  }

  pan(dx: number, dz: number): void {
    const fwdX = -Math.sin(this.azimuth);
    const fwdZ = -Math.cos(this.azimuth);
    const rightX = fwdZ;
    const rightZ = -fwdX;
    const speed = this.distance * 0.02;
    this.target.x += (rightX * dx + fwdX * dz) * speed;
    this.target.z += (rightZ * dx + fwdZ * dz) * speed;
  }
}
